import { useEffect, useState } from 'react';

/**
 * What a person sees when they belong to no organisation.
 *
 * The spec's fourth role is the individual user, who "sees only their own devices". The API has
 * had /v1/dashboard/mine — the one route that needs no permission — since the dashboard was
 * built, and the client has exposed getMyDevices() for as long. Nothing rendered it: every
 * screen in the shell required a permission an individual does not have, so signing in as one
 * showed a single sentence and nothing else.
 */

export interface MyDevices {
  devices: { id: string; assetTag: string; make: string; model: string; status: string; at: string; warrantyEnds: string | null }[];
  openRepairs: { ticketId: string; assetTag: string; status: string; fault: string }[];
}

export interface MyDevicesApi {
  getMyDevices: () => Promise<MyDevices>;
}

const LABELS: Record<string, string> = {
  procured: 'Procured', in_stock: 'In stock', assigned: 'Assigned', in_service: 'In service',
  under_repair: 'Under repair', awaiting_parts: 'Awaiting parts', returned: 'Returned',
  retired: 'Retired', disposed: 'Disposed',
  raised: 'Raised', triaged: 'Triaged', dispatched: 'Dispatched', in_repair: 'In repair',
};
const label = (k: string) => LABELS[k] ?? k;

/** A date a person can read, or nothing at all rather than "null". */
const readable = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

type Load = { kind: 'loading' } | { kind: 'ready'; data: MyDevices } | { kind: 'error'; message: string };

export function MyDevices({ api, hasOrg }: { api: MyDevicesApi; hasOrg: boolean }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    api.getMyDevices()
      .then((data) => live && setLoad({ kind: 'ready', data }))
      .catch((e: unknown) => live && setLoad({
        kind: 'error', message: e instanceof Error ? e.message : 'unknown error',
      }));
    return () => { live = false; };
  }, [api]);

  if (load.kind === 'loading') return <p>Loading your devices…</p>;
  if (load.kind === 'error') return <p data-testid="mine-error">Could not load your devices: {load.message}</p>;

  const { devices, openRepairs } = load.data;

  return (
    <section>
      <h2>Your devices</h2>
      <p>
        {hasOrg
          ? 'Hardware assigned to you.'
          : 'Hardware assigned to you. You do not belong to an organisation yet, so this is everything you can see.'}
      </p>

      {devices.length === 0 ? (
        <p data-testid="mine-empty">
          Nothing is assigned to you yet. When your organisation hands you a laptop or a phone, it
          appears here with its warranty and repair history.
        </p>
      ) : (
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th scope="col">Asset tag</th>
                <th scope="col">Device</th>
                <th scope="col">Status</th>
                <th scope="col">Warranty</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id} data-testid={`mine-${d.assetTag}`}>
                  <td><strong>{d.assetTag}</strong></td>
                  <td>{d.make} {d.model}</td>
                  <td><span className={`pill ${d.status}`}>{label(d.status)}</span></td>
                  <td>{readable(d.warrantyEnds) ?? 'Not recorded'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Repairs in progress</h3>
      {openRepairs.length === 0
        ? <p data-testid="mine-no-repairs">Nothing of yours is in for repair.</p>
        : (
          <ul>
            {openRepairs.map((r) => (
              <li key={r.ticketId}>
                {r.assetTag} — {r.fault}
                <span className={`pill ${r.status}`}>{label(r.status)}</span>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}
